/*
 * Kept in step with the backend's src/services/media/mp3Audio.ts (transcripts read
 * the same recordings on the server); a fix in one belongs in the other.
 */
/**
 * MP3 recordings (MPEG-1, 2 and 2.5 Layer III), read frame by frame without
 * downloading the file.
 *
 * An MP3 has no index: frame k's position is only known by walking the frames
 * before it. So:
 *  - the header is read once (ID3v2 skipped; the Xing/Info, VBRI and LAME
 *    tags give the frame count and the encoder's delay and padding);
 *  - a constant-bit-rate file is seeked by arithmetic (frame k starts at
 *    audioStart + k · frameBytes, within a byte, because encoders pad frames
 *    to keep the rate exact), then checked against the real frame there and
 *    every frame after it;
 *  - a variable-bit-rate file is walked once, start to end, which counts its
 *    frames and leaves a small seek index (one offset every 1000 frames, about
 *    26 seconds) so later reads start near where they need to be.
 *
 * The result is an ordinary AudioTrack whose packet table holds the frames of
 * the stretch asked for, with exact times. Times are presentation seconds as
 * players show them: when a LAME tag gives the encoder delay, the first
 * delay + 529 samples (the decoder's own delay) are skipped, as FFmpeg,
 * browsers and Whisper's decoder do.
 */

import type { ByteSource } from './byteSource';
import { AudioTrack, MediaReadError, Mp3Codec, PacketTable } from './isoAudio';

export interface Mp3FrameHeader {
  /** MPEG version: 1, 2 or 25 (2.5). */
  mpeg: 1 | 2 | 25;
  bitrateKbps: number;
  sampleRate: number;
  channels: 1 | 2;
  /** Samples per channel in the frame. */
  samples: number;
  /** Bytes, header included. */
  size: number;
  /** Bytes of side information after the header (and its CRC). */
  sideInfo: number;
  crc: boolean;
}

export interface Mp3Layout {
  /** First audio frame; a Xing/Info/VBRI tag frame, when there is one, comes before it. */
  audioStart: number;
  /** End of the MPEG data: the file's end, less an ID3v1 or APE tag. */
  dataEnd: number;
  mpeg: 1 | 2 | 25;
  sampleRate: number;
  channels: number;
  samplesPerFrame: number;
  /** Audio frames in the file: from a tag, from CBR arithmetic, or counted; null when unknown. */
  frameCount: number | null;
  /** Samples skipped at the start: the encoder delay from a LAME tag, plus 529 for the decoder. */
  skipSamples: number;
  /** Samples trimmed from the end (the LAME tag's padding, less the decoder delay). */
  endPaddingSamples: number;
  /** Every frame at one bit rate: frame k starts at audioStart + k · frameBytes (within a byte). */
  cbr: { bitrateKbps: number; frameBytes: number } | null;
}

export interface Mp3SeekIndex {
  /** Frames between entries. */
  every: number;
  /** Byte offset of frame i · every. */
  offsets: number[];
  /** Audio frames counted. */
  frames: number;
}

export interface Mp3AudioTrack extends AudioTrack {
  mp3: Mp3Layout;
  /** The seek index, when this read walked the whole file or was given one. */
  index: Mp3SeekIndex | null;
}

export interface Mp3ReadOptions {
  signal?: AbortSignal;
  /** Presentation seconds the packet table must cover; default the whole file. */
  fromS?: number;
  toS?: number;
  /** The layout from an earlier read of this file. */
  layout?: Mp3Layout;
  /** A seek index from an earlier full walk (variable-bit-rate files). */
  index?: Mp3SeekIndex | null;
  /** Walk the whole file to count frames and build a seek index (variable bit rate only). */
  buildIndex?: boolean;
  /** Bytes per read while walking frames. */
  blockBytes?: number;
  /** Refuse (MediaReadError) rather than walk more than this many bytes. */
  maxScanBytes?: number;
}

const BITRATES_MPEG1 = [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320];
const BITRATES_MPEG2 = [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160];
const SAMPLE_RATES: Record<number, number[]> = { 1: [44100, 48000, 32000], 2: [22050, 24000, 16000], 25: [11025, 12000, 8000] };
/** The MP3 decoder's own delay, in samples. */
const DECODER_DELAY = 529;
const INDEX_EVERY = 1000;
const DEFAULT_BLOCK_BYTES = 1024 * 1024;
/** How far past the ID3 tag (or junk) the first frame is looked for. */
const FIRST_FRAME_SEARCH_BYTES = 256 * 1024;
/** Frames read before the stretch asked for: a frame's data can start in the frames before it. */
const LEAD_IN_FRAMES = 4;

const ascii = (data: Uint8Array, at: number, length: number): string => {
  let text = '';
  for (let i = 0; i < length; i += 1) text += String.fromCharCode(data[at + i]);
  return text;
};
const u32be = (data: Uint8Array, at: number) =>
  data[at] * 0x1000000 + ((data[at + 1] << 16) | (data[at + 2] << 8) | data[at + 3]);

/** The Layer III frame header at `at`, or null when there isn't a valid one. */
export const parseMp3FrameHeader = (data: Uint8Array, at: number): Mp3FrameHeader | null => {
  if (at < 0 || at + 4 > data.length) return null;
  const b1 = data[at + 1];
  const b2 = data[at + 2];
  const b3 = data[at + 3];
  if (data[at] !== 0xff || (b1 & 0xe0) !== 0xe0) return null;
  const versionBits = (b1 >> 3) & 3;
  if (versionBits === 1) return null;
  if (((b1 >> 1) & 3) !== 1) return null; // Layer III only
  const bitrateIndex = b2 >> 4;
  const rateIndex = (b2 >> 2) & 3;
  if (bitrateIndex === 0 || bitrateIndex === 15 || rateIndex === 3 || (b3 & 3) === 2) return null;
  const mpeg: 1 | 2 | 25 = versionBits === 3 ? 1 : versionBits === 2 ? 2 : 25;
  const channels: 1 | 2 = b3 >> 6 === 3 ? 1 : 2;
  const bitrateKbps = (mpeg === 1 ? BITRATES_MPEG1 : BITRATES_MPEG2)[bitrateIndex];
  const sampleRate = SAMPLE_RATES[mpeg][rateIndex];
  const samples = mpeg === 1 ? 1152 : 576;
  const padding = (b2 >> 1) & 1;
  return {
    mpeg,
    bitrateKbps,
    sampleRate,
    channels,
    samples,
    size: Math.floor(((samples / 8) * bitrateKbps * 1000) / sampleRate) + padding,
    sideInfo: mpeg === 1 ? (channels === 1 ? 17 : 32) : channels === 1 ? 9 : 17,
    crc: (b1 & 1) === 0,
  };
};

const sameStream = (a: Mp3FrameHeader, b: Mp3FrameHeader) => a.mpeg === b.mpeg && a.sampleRate === b.sampleRate;

/** True for the first bytes of an MP3: an ID3v2 tag, or a Layer III frame header. */
export const isMp3Header = (head: Uint8Array): boolean =>
  (head.length >= 3 && head[0] === 0x49 && head[1] === 0x44 && head[2] === 0x33) || parseMp3FrameHeader(head, 0) !== null;

/** Reads through a file in blocks, keeping the current one. */
class BlockReader {
  private block: Uint8Array = new Uint8Array(0);
  private start = 0;
  /** Bytes fetched so far. */
  fetched = 0;

  constructor(private readonly source: ByteSource, private readonly blockBytes: number, private readonly signal?: AbortSignal) {}

  /** The current block and the index of `offset` in it, with at least `length` bytes there (fewer at the end of the file). */
  async at(offset: number, length: number): Promise<[Uint8Array, number]> {
    if (offset < this.start || offset + length > this.start + this.block.length) {
      const wanted = Math.min(Math.max(length, this.blockBytes), Math.max(0, this.source.size - offset));
      this.block = wanted > 0 ? await this.source.read(offset, wanted, this.signal) : new Uint8Array(0);
      this.start = offset;
      this.fetched += this.block.length;
    }
    return [this.block, offset - this.start];
  }
}

const notMp3 = () => new MediaReadError('not-mp4', 'This file is not an MP3.');

/** The frame header at `offset` when the frame after it is also a frame of the same stream. */
const chainedFrame = async (reader: BlockReader, offset: number, dataEnd: number): Promise<Mp3FrameHeader | null> => {
  let [block, at] = await reader.at(offset, 4);
  const header = parseMp3FrameHeader(block, at);
  if (!header) return null;
  const next = offset + header.size;
  if (next === dataEnd) return header;
  if (next + 4 > dataEnd) return null;
  [block, at] = await reader.at(next, 4);
  const following = parseMp3FrameHeader(block, at);
  return following && sameStream(header, following) ? header : null;
};

/** The first chained frame at or after `from`, within `limit` bytes; -1 when there is none. */
const findFrame = async (reader: BlockReader, from: number, limit: number, dataEnd: number): Promise<number> => {
  const end = Math.min(dataEnd - 4, from + limit);
  for (let offset = from; offset <= end; offset += 1) {
    const [block, at] = await reader.at(offset, 4);
    if (block[at] === 0xff && (block[at + 1] & 0xe0) === 0xe0 && (await chainedFrame(reader, offset, dataEnd))) return offset;
  }
  return -1;
};

/** Where the MPEG data ends: before an ID3v1 tag and an APEv2 tag at the end of the file. */
const findDataEnd = async (source: ByteSource, signal?: AbortSignal): Promise<number> => {
  let end = source.size;
  if (end >= 128) {
    const tail = await source.read(end - 128, 128, signal);
    if (ascii(tail, 0, 3) === 'TAG') end -= 128;
  }
  if (end >= 32) {
    const footer = await source.read(end - 32, 32, signal);
    if (ascii(footer, 0, 8) === 'APETAGEX') {
      const size = footer[12] | (footer[13] << 8) | (footer[14] << 16) | (footer[15] << 24);
      const hasHeader = (footer[23] & 0x80) !== 0;
      if (size > 0 && size + (hasHeader ? 32 : 0) <= end) end -= size + (hasHeader ? 32 : 0);
    }
  }
  return end;
};

/** Reads an MP3's layout: where its frames are, what they hold and how many there are. */
export const readMp3Layout = async (source: ByteSource, signal?: AbortSignal): Promise<Mp3Layout> => {
  const reader = new BlockReader(source, 64 * 1024, signal);
  const dataEnd = await findDataEnd(source, signal);
  let offset = 0;
  // ID3v2 tags (there can be several, and cover art makes them large).
  for (let tags = 0; tags < 8 && offset + 10 <= dataEnd; tags += 1) {
    const [block, at] = await reader.at(offset, 10);
    if (ascii(block, at, 3) !== 'ID3') break;
    const size = ((block[at + 6] & 0x7f) << 21) | ((block[at + 7] & 0x7f) << 14) | ((block[at + 8] & 0x7f) << 7) | (block[at + 9] & 0x7f);
    offset += 10 + size + ((block[at + 5] & 0x10) !== 0 ? 10 : 0);
  }
  const first = await findFrame(reader, offset, FIRST_FRAME_SEARCH_BYTES, dataEnd);
  if (first < 0) throw notMp3();
  const [block, at] = await reader.at(first, 2048);
  const header = parseMp3FrameHeader(block, at) as Mp3FrameHeader;

  let frameCount: number | null = null;
  let skipSamples = 0;
  let endPaddingSamples = 0;
  let audioStart = first;
  let tag: 'Xing' | 'Info' | 'VBRI' | null = null;

  const xingAt = at + 4 + (header.crc ? 2 : 0) + header.sideInfo;
  const xingId = xingAt + 8 <= block.length ? ascii(block, xingAt, 4) : '';
  if (xingId === 'Xing' || xingId === 'Info') {
    tag = xingId;
    const flags = u32be(block, xingAt + 4);
    let field = xingAt + 8;
    if (flags & 1) {
      frameCount = u32be(block, field);
      field += 4;
    }
    if (flags & 2) field += 4;
    if (flags & 4) field += 100;
    if (flags & 8) field += 4;
    // The LAME extension: a short encoder name, then the delay and padding.
    if (field + 24 <= block.length && /^[A-Za-z]{4}/.test(ascii(block, field, 4))) {
      const delay = (block[field + 21] << 4) | (block[field + 22] >> 4);
      const padding = ((block[field + 22] & 0x0f) << 8) | block[field + 23];
      if (delay > 0 || padding > 0) {
        skipSamples = delay + DECODER_DELAY;
        endPaddingSamples = Math.max(0, padding - DECODER_DELAY);
      }
    }
  } else if (at + 36 + 26 <= block.length && ascii(block, at + 36, 4) === 'VBRI') {
    tag = 'VBRI';
    frameCount = u32be(block, at + 36 + 14);
  }
  // The tag frame holds no sound.
  if (tag) audioStart = first + header.size;
  if (frameCount !== null && !(frameCount > 0)) frameCount = null;

  // Constant bit rate: LAME marks it 'Info' (whose tag frame may use another
  // rate, big enough to hold the tag); untagged files may be either. Both
  // are checked over their first audio frames, and every frame read later
  // is checked again.
  let cbr: Mp3Layout['cbr'] = null;
  if (tag !== 'Xing' && tag !== 'VBRI') {
    let constant = true;
    let rate = 0;
    let position = audioStart;
    for (let i = 0; i < 16 && position + 4 <= dataEnd; i += 1) {
      const frame = await chainedFrame(reader, position, dataEnd);
      if (!frame) break;
      if (!rate) rate = frame.bitrateKbps;
      if (frame.bitrateKbps !== rate || !sameStream(frame, header)) {
        constant = false;
        break;
      }
      position += frame.size;
    }
    if (constant && rate) cbr = { bitrateKbps: rate, frameBytes: ((header.samples / 8) * rate * 1000) / header.sampleRate };
  }
  if (cbr && frameCount === null) frameCount = Math.max(1, Math.floor((dataEnd - audioStart + 4) / cbr.frameBytes));

  return {
    audioStart,
    dataEnd,
    mpeg: header.mpeg,
    sampleRate: header.sampleRate,
    channels: header.channels,
    samplesPerFrame: header.samples,
    frameCount,
    skipSamples,
    endPaddingSamples,
    cbr,
  };
};

/** Presentation seconds of the whole file (estimated from the first frame's rate when the frame count is unknown). */
export const mp3DurationSeconds = (layout: Mp3Layout, firstBitrateKbps?: number): number => {
  if (layout.frameCount !== null) {
    return Math.max(0, (layout.frameCount * layout.samplesPerFrame - layout.skipSamples - layout.endPaddingSamples) / layout.sampleRate);
  }
  const kbps = layout.cbr?.bitrateKbps ?? firstBitrateKbps ?? 128;
  return Math.max(0, ((layout.dataEnd - layout.audioStart) * 8) / (kbps * 1000));
};

/** Thrown inside a walk when a "constant bit rate" file turns out not to be (a marker, not a subclass, so it survives any transpiler). */
const NOT_CONSTANT = 'mp3-rate-changed';
const notConstant = () => Object.assign(new Error('The bit rate changed.'), { reason: NOT_CONSTANT });

class PacketCollector {
  times: number[] = [];
  offsets: number[] = [];
  sizes: number[] = [];

  push(frame: number, samplesPerFrame: number, offset: number, size: number) {
    this.times.push(frame * samplesPerFrame);
    this.offsets.push(offset);
    this.sizes.push(size);
  }

  table(samplesPerFrame: number): PacketTable {
    const count = this.times.length;
    const durations = new Float64Array(count);
    durations.fill(samplesPerFrame);
    return {
      kind: 'packets',
      count,
      times: Float64Array.from(this.times),
      durations,
      offsets: Float64Array.from(this.offsets),
      sizes: Uint32Array.from(this.sizes),
    };
  }
}

/**
 * The frames covering presentation seconds [fromS, toS] (plus a few before,
 * for the decoder), as a packet table with exact times. A variable-bit-rate
 * file with no seek index is walked from the start; `buildIndex` walks it to
 * the end and returns the index for next time.
 */
export const readMp3Track = async (source: ByteSource, options: Mp3ReadOptions = {}): Promise<Mp3AudioTrack> => {
  const { signal } = options;
  const layout = options.layout ?? (await readMp3Layout(source, signal));
  const spf = layout.samplesPerFrame;
  const frameOf = (seconds: number) => Math.floor((Math.max(0, seconds) * layout.sampleRate + layout.skipSamples) / spf);
  const fromS = options.fromS ?? 0;
  const toS = options.toS ?? Infinity;
  const firstWanted = Math.max(0, frameOf(fromS) - LEAD_IN_FRAMES);
  const endWanted = Number.isFinite(toS) ? frameOf(toS) + 2 : Infinity;
  let index = options.index && options.index.offsets.length > 0 ? options.index : null;
  const buildIndex = Boolean(options.buildIndex) && !layout.cbr && !index;
  const reader = new BlockReader(source, options.blockBytes ?? DEFAULT_BLOCK_BYTES, signal);
  const maxScan = options.maxScanBytes ?? Infinity;

  const walk = async (cbr: Mp3Layout['cbr'], countAll: boolean) => {
    // Where to start: frame 0, the nearest index entry, or (CBR) the computed offset.
    let frame = 0;
    let offset = layout.audioStart;
    if (!countAll && firstWanted > 0) {
      if (cbr) {
        const estimate = layout.audioStart + Math.floor(firstWanted * cbr.frameBytes);
        const found = await findFrame(reader, Math.max(layout.audioStart, estimate - 8), Math.ceil(cbr.frameBytes) * 3 + 64, layout.dataEnd);
        if (found < 0) throw new MediaReadError('corrupt', 'The MP3 could not be read at that point.');
        offset = found;
        frame = Math.round((found - layout.audioStart) / cbr.frameBytes);
      } else if (index) {
        const entry = Math.min(Math.floor(firstWanted / index.every), index.offsets.length - 1);
        const candidate = index.offsets[entry];
        if (await chainedFrame(reader, candidate, layout.dataEnd)) {
          offset = candidate;
          frame = entry * index.every;
        }
      }
    }
    const packets = new PacketCollector();
    const offsets: number[] = [];
    const startFetched = reader.fetched;
    while (offset + 4 <= layout.dataEnd) {
      if (!countAll && frame >= endWanted) break;
      if (reader.fetched - startFetched > maxScan) {
        throw new MediaReadError('unsupported-audio', 'This MP3 would have to be read in full first.');
      }
      const [block, at] = await reader.at(offset, 4);
      const header = parseMp3FrameHeader(block, at);
      if (!header || header.sampleRate !== layout.sampleRate || header.mpeg !== layout.mpeg) {
        // Damage or junk between frames: skip to the next real frame.
        const next = await findFrame(reader, offset + 1, 64 * 1024, layout.dataEnd);
        if (next < 0) break;
        offset = next;
        continue;
      }
      if (cbr && header.bitrateKbps !== cbr.bitrateKbps) throw notConstant();
      if (offset + header.size > layout.dataEnd) break; // a cut-off last frame
      if (countAll && frame % INDEX_EVERY === 0) offsets.push(offset);
      if (frame >= firstWanted && frame < endWanted) packets.push(frame, spf, offset, header.size);
      frame += 1;
      offset += header.size;
    }
    return { packets, offsets, frames: frame };
  };

  let cbr = layout.cbr;
  let result;
  try {
    result = await walk(cbr, buildIndex);
  } catch (error) {
    if ((error as { reason?: string } | null)?.reason !== NOT_CONSTANT) throw error;
    // Not constant after all: walk it as variable bit rate.
    cbr = null;
    result = await walk(null, true);
  }
  let resolvedLayout = layout;
  if (!cbr && layout.cbr) resolvedLayout = { ...layout, cbr: null };
  if (result.offsets.length > 0) {
    index = { every: INDEX_EVERY, offsets: result.offsets, frames: result.frames };
    resolvedLayout = { ...resolvedLayout, frameCount: result.frames };
  } else if (index && resolvedLayout.frameCount === null) {
    resolvedLayout = { ...resolvedLayout, frameCount: index.frames };
  }

  const codec: Mp3Codec = { kind: 'mp3', sampleRate: layout.sampleRate, channels: layout.channels };
  const table = result.packets.table(spf);
  return {
    codec,
    timescale: layout.sampleRate,
    mediaStart: layout.skipSamples,
    startDelay: 0,
    table,
    fragmented: false,
    durationSeconds: mp3DurationSeconds(resolvedLayout),
    complete: true,
    mp3: resolvedLayout,
    index,
  };
};

export const isMp3Track = (track: AudioTrack): track is Mp3AudioTrack =>
  Boolean((track as Mp3AudioTrack).mp3) && track.codec.kind === 'mp3';
